import { useNavigate, useSearchParams } from "react-router-dom";
import { History, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { withWorkspaceContext } from "@/lib/workspace-navigation";

/**
 * Runs / Evaluation switcher for a workflow.
 *
 * These are two different kinds of reading. The runs list is a scannable
 * table you come to with a specific run in mind; an evaluation is a page of
 * prose you sit and read. Stacking the second above the first pushed the
 * table below the fold and made both worse, so they get their own routes.
 */
export function WorkflowTabs({
  tenantId,
  workflowId,
  active,
}: {
  tenantId: string;
  workflowId: string;
  active: "runs" | "evaluation";
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const go = (tab: "runs" | "evaluation") => {
    if (tab === active) return;
    navigate(
      withWorkspaceContext(
        `/tenants/${tenantId}/workflows/${workflowId}/${tab}`,
        searchParams,
      ),
    );
  };

  const tabs = [
    { id: "runs" as const, label: "Runs", Icon: History },
    { id: "evaluation" as const, label: "Evaluation", Icon: Sparkles },
  ];

  return (
    <div className="flex items-center gap-0.5">
      {tabs.map(({ id, label, Icon }) => (
        <button
          key={id}
          onClick={() => go(id)}
          className={cn(
            "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            active === id
              ? "bg-surface-hover text-text-primary"
              : "text-text-muted hover:bg-surface-hover hover:text-text-primary",
          )}
        >
          <Icon size={13} />
          {label}
        </button>
      ))}
    </div>
  );
}
